import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/layouts/AdminLayout";
import { FormDialog } from "@/components/FormDialog";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { pick } from "@/lib/forms";
import { cn } from "@/lib/utils";
import { KitComponentsDialog } from "@/components/catalog/KitComponentsDialog";

const typeFields = (meta) => [
  { name: "name", label: "Nombre del tipo", required: true, placeholder: "Ej. Cámara IP" },
  { name: "business_unit_code", label: "Unidad de negocio", type: "select", options: (meta?.business_units || []).map((b) => ({ value: b.code, label: b.name })) },
  { name: "classification", label: "Clasificación", type: "select", default: "equipo", options: (meta?.item_classes || []).map((c) => ({ value: c.code, label: c.name })) },
  { name: "unit", label: "Unidad de medida", type: "select", default: "Pieza", options: (meta?.units || ["Pieza"]).map((u) => ({ value: u, label: u })) },
  { name: "description", label: "Descripción (opcional)", type: "textarea", rows: 2 },
  { name: "order", label: "Orden", type: "number", default: 100 },
  { name: "active", label: "Activo", type: "switch" },
];

const modelFields = (types, sel) => [
  { name: "type_id", label: "Tipo de equipamiento", type: "select", options: types.map((t) => ({ value: t.id, label: t.name })) },
  { name: "brand", label: "Marca", required: true },
  { name: "model", label: "Modelo", required: true },
  { name: "description", label: "Descripción" },
  { name: "specs", label: "Características técnicas (opcional)", type: "textarea", rows: 3 },
  ...(sel?.name === "Dashcam" ? [
    { name: "integrated_cameras", label: "Cámaras integradas en el cuerpo principal (se documentan con la foto del equipo)", type: "number" },
    { name: "integrated_cameras_detail", label: "Detalle de cámaras integradas (ej. frontal e interior)" },
    { name: "included_cameras", label: "Cámaras independientes incluidas de fábrica (cada una pide su foto)", type: "number" },
    { name: "included_cameras_location", label: "Ubicación de las cámaras incluidas (ej. cabina)" },
    { name: "max_extra_cameras", label: "Cámaras adicionales que admite (vacío = capacidad no registrada)", type: "number" },
  ] : []),
  ...(sel?.name === "Accesorios de Videovigilancia Móvil" ? [{ name: "is_camera", label: "Es cámara (pide foto propia al instalarse)", type: "switch" }] : []),
  { name: "active", label: "Activo", type: "switch" },
];

const TypeList = ({ types, sel, setSel }) => (
  <div className="space-y-1" data-testid="equipment-type-list">
    {types.map((t) => (
      <button key={t.id} onClick={() => setSel(t)} data-testid={`equipment-type-${t.id}`}
        className={cn("flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm transition-colors",
          sel?.id === t.id ? "bg-primary/10 text-primary" : "hover:bg-secondary", !t.active && "opacity-50")}>
        <span className="flex-1">{t.name}{!t.active && " (inactivo)"}</span>
        <span className="font-mono text-xs text-muted-foreground">{t.models_count}</span>
      </button>
    ))}
  </div>
);

export default function EquipmentCatalog() {
  const { meta, can } = useAuth();
  const editable = can("catalog.write");
  const [bu, setBu] = useState(null);
  const [types, setTypes] = useState([]);
  const [sel, setSel] = useState(null);
  const [models, setModels] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => { if (!bu && meta?.business_units?.length) setBu(meta.business_units[0].code); }, [meta, bu]);
  useEffect(() => {
    if (!bu) return;
    api.get("/catalog/types", { params: { business_unit_code: bu } }).then((r) => {
      setTypes(r.data);
      setSel((s) => r.data.find((t) => t.id === s?.id) || r.data[0] || null);
    });
  }, [bu, tick]);
  useEffect(() => {
    if (!sel) return setModels([]);
    api.get("/catalog/models", { params: { type_id: sel.id } }).then((r) => setModels(r.data));
  }, [sel, tick]);

  const saveType = async (form) => {
    if (dlg.item) await api.patch(`/catalog/types/${dlg.item.id}`, form);
    else await api.post("/catalog/types", form);
    toast.success("Tipo de equipamiento guardado");
    if (form.business_unit_code && form.business_unit_code !== bu) setBu(form.business_unit_code);
    setTick((t) => t + 1);
  };
  const saveModel = async (form) => {
    if (dlg.item) await api.patch(`/catalog/models/${dlg.item.id}`, form);
    else await api.post("/catalog/models", form);
    toast.success("Modelo guardado");
    setTick((t) => t + 1);
  };
  const tFields = typeFields(meta);
  const mFields = modelFields(types, sel);

  return (
    <div>
      <PageHeader eyebrow="Biblioteca reutilizable · tipo → modelo" title="Catálogo de equipos" />
      <div className="mb-5 flex gap-1.5 overflow-x-auto">
        {(meta?.business_units || []).map((b) => (
          <button key={b.code} onClick={() => setBu(b.code)} data-testid={`catalog-bu-${b.code}`}
            className={cn("h-10 shrink-0 rounded-full border px-5 text-sm font-medium", bu === b.code ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground")}>{b.name}</button>
        ))}
      </div>
      <div className="grid gap-5 lg:grid-cols-[300px_1fr]">
        <section className="rounded-xl border border-border bg-card/40 p-3">
          <div className="mb-2 flex items-center justify-between px-2">
            <h2 className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Tipos ({types.length})</h2>
            {editable && <Button size="sm" variant="outline" onClick={() => setDlg({ kind: "type" })} data-testid="new-equipment-type-button"><Plus className="mr-1 h-3 w-3" />Tipo</Button>}
          </div>
          <TypeList types={types} sel={sel} setSel={setSel} />
        </section>
        <section className="min-w-0 rounded-xl border border-border bg-card/40 p-5" data-testid="equipment-models-panel">
          {!sel ? <p className="text-sm text-muted-foreground">Selecciona un tipo de equipamiento.</p> : (
            <>
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2"><Layers className="h-4 w-4 text-primary" /><h2 className="font-display text-xl font-semibold" data-testid="selected-type-name">{sel.name}</h2></div>
                  <p className="mt-1 text-xs text-muted-foreground">{sel.description || "Sin descripción"} · Unidad: {sel.unit} · v{sel.version || 1}</p>
                </div>
                {editable && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setDlg({ kind: "type", item: sel })} data-testid="edit-equipment-type-button"><Pencil className="mr-1 h-3.5 w-3.5" />Editar tipo</Button>
                    <Button size="sm" onClick={() => setDlg({ kind: "model" })} data-testid="new-equipment-model-button"><Plus className="mr-1 h-3.5 w-3.5" />Modelo</Button>
                  </div>
                )}
              </div>
              <div className="overflow-x-auto rounded-lg border border-border">
                <Table data-testid="equipment-models-table">
                  <TableHeader><TableRow className="hover:bg-transparent">{["Marca", "Modelo", "Descripción", "Características", "Estado", ""].map((h) => <TableHead key={h} className="font-mono text-[11px] uppercase tracking-wider">{h}</TableHead>)}</TableRow></TableHeader>
                  <TableBody>
                    {models.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">Sin modelos. En la OS se puede usar "Sin modelo especificado".</TableCell></TableRow>}
                    {models.map((m) => (
                      <TableRow key={m.id} data-testid={`equipment-model-row-${m.id}`}>
                        <TableCell>{m.brand}</TableCell>
                        <TableCell className="font-mono text-sm font-semibold">{m.model}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{m.description}</TableCell>
                        <TableCell className="max-w-[240px] truncate text-xs text-muted-foreground">{m.specs || "—"}</TableCell>
                        <TableCell><span className={m.active ? "text-emerald-400" : "text-muted-foreground"}>{m.active ? "Activo" : "Inactivo"}</span></TableCell>
                        <TableCell><div className="flex gap-1">{editable && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDlg({ kind: "model", item: m })} data-testid={`edit-equipment-model-${m.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
                          {editable && (m.kit_components?.length > 0 || (sel?.name === "MDVR" && sel?.business_unit_code === "gps")) && <Button size="sm" variant="outline" className="h-8" onClick={() => setDlg({ kind: "kit", item: m })} data-testid={`kit-components-${m.id}`}>Composición</Button>}</div></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </section>
      </div>
      {dlg?.kind === "kit" && <KitComponentsDialog model={dlg.item} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); setTick((t) => t + 1); }} />}
      <FormDialog open={dlg?.kind === "type"} onOpenChange={(o) => !o && setDlg(null)} title={dlg?.item ? "Editar tipo de equipamiento" : "Nuevo tipo de equipamiento"}
        fields={tFields} initial={dlg?.item ? pick(dlg.item, tFields) : { business_unit_code: bu }} onSubmit={saveType} testid="equipment-type-form" />
      <FormDialog open={dlg?.kind === "model"} onOpenChange={(o) => !o && setDlg(null)} title={dlg?.item ? "Editar modelo" : `Nuevo modelo · ${sel?.name || ""}`}
        fields={mFields} initial={dlg?.item ? pick(dlg.item, mFields) : { type_id: sel?.id }} onSubmit={saveModel} testid="equipment-model-form" />
    </div>
  );
}
